import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { updateGlobalState } from "../../state/global/slice";
import { useNavigate, useLocation } from "react-router";
import { useAuth } from "../../features/auth/AuthContext";
import Integrations from "./Integrations";
import ScheduleMeeting from "./ScheduleMeeting";
import UpcomingMeeting from "./UpcomingMeeting";
import MeetingHistory from "./MeetingHistory";
import ScheduleSuccess from "./ScheduleSuccess";
import "./bookAMeeting.css";
import FloatingAIButton from "../FloatingAIButton/FloatingAIButton";
import { setDrawerState, setIsWebAllowed, setQueryMetaData } from "../../state/reducers/chatDrawerReducers";
import BackButton from "../BackButton/BackButton";
import { getUpcomingMeetings } from "./meetingService";

const Meetings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useAuth();
  const globalState = useSelector((state) => state.globalState);
  const role = globalState?.role;
  const isStudent = role === "Student";
  const dispatch = useDispatch();
  const chatDrawerState = useSelector((state) => state?.ChatDrawerState);

  const [refreshKey, setRefreshKey] = useState(0);
  const bumpRefresh = () => setRefreshKey((k) => k + 1);

  const [upcomingMeetings, setUpcomingMeetings] = useState([]);
  const [upcomingLoading, setUpcomingLoading] = useState(true);
  const [upcomingError, setUpcomingError] = useState("");
  const [upcomingCount, setUpcomingCount] = useState(0);

  // Get initial values from globalState (already populated during app init)
  const connFlag =
    globalState?.student_details?.data?.has_google_meet_credential ||
    globalState?.teacher_details?.data?.has_google_meet_credential ||
    false;
  const acctName =
    globalState?.student_details?.data?.google_account ??
    globalState?.teacher_details?.data?.google_account ??
    "";

  const [googleConnected, setGoogleConnected] = useState(!!connFlag);
  const [googleAccount, setGoogleAccount] = useState(acctName);

  useEffect(() => {
    setGoogleConnected(!!connFlag);
  }, [connFlag]);

  const teacherDetails = globalState?.teacher_details;
  const teacherAvailable = !!teacherDetails?.data?.is_available_for_meet;
  const setTeacherAvailable = (next) => {
    if (!teacherDetails) return;
    dispatch(
      updateGlobalState({
        key: "teacher_details",
        value: {
          ...teacherDetails,
          data: { ...teacherDetails.data, is_available_for_meet: next },
        },
      })
    );
  };

  const tabs = useMemo(() => {
    const upcomingLabel = isStudent ? "Upcoming Meetings" : "Scheduled Meetings";
    const base = [
      { key: "upcoming", label: upcomingLabel },
      { key: "history", label: "Meeting History" },
    ];
      return isStudent
    ? base
    : [{ key: "settings", label: "Settings" }, ...base];
  }, [isStudent]);

  const [activeTab, setActiveTab] = useState(tabs[0].key);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (!tabs.some((t) => t.key === activeTab)) setActiveTab(tabs[0].key);
  }, [tabs, activeTab]);

  // Fetch upcoming meetings once and reuse for both count and UpcomingMeeting content
  useEffect(() => {
    let alive = true;
    (async () => {
      setUpcomingLoading(true);
      const res = await getUpcomingMeetings(token);
      if (!alive) return;
      setUpcomingLoading(false);
      if (res.ok) {
        const list = res.data || [];
        setUpcomingMeetings(list);
        setUpcomingCount(list.length);
      } else {
        setUpcomingError(res.error || "");
      }
    })();
    return () => { alive = false; };
  }, [token, refreshKey]);

  // Handle tab click: refetch only when user actually clicks into "Upcoming" from elsewhere
  const handleTabClick = (tabKey) => {
    if (tabKey === "upcoming" && activeTab !== "upcoming") {
      bumpRefresh(); // real user click, not a side-effect state change
    }
    setActiveTab(tabKey);
  };

  useEffect(() => {
    // Notification redirect takes priority over default
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    } else {
    const def = !googleConnected
      ? "settings"
      : isStudent
      ? "upcoming"
      : "settings";
    setActiveTab(def);
    }
  }, [googleConnected, isStudent, location.state?.activeTab]);

  const contentLocked =
    !googleConnected && (activeTab === "schedule" || activeTab === "upcoming");

  const tabLabel = (t) =>
    t.key === "upcoming" && upcomingCount > 0
      ? `${t.label} (${String(upcomingCount).padStart(2, "0")})`
      : t.label;

  const handlesupportticket = () => {
    navigate("/SupportRequest")
  }
  return (
    <div className="meetings-page-container" style={{paddingLeft: isStudent ? "60px" : "0", paddingRight: isStudent ? "60px" : "0"}}>
      <BackButton buttonName={"Back"} fallbackPath="/dashboard/home" />
      <div className="meetings-page-header">
        <h2>My Meetings</h2>
        <div className="need-help">
        <h4 onClick={handlesupportticket}>Need help?</h4>
        </div>
      </div>

      <div className="bm-tabs">
        {tabs.map((t) => (
          <button
            type="button"
            key={t.key}
            className={`bm-tab ${activeTab === t.key ? "bm-tab-active" : ""}`}
            onClick={() => handleTabClick(t.key)}
          >
            {tabLabel(t)}
          </button>
        ))}
      </div>

      <div className={`bm-tab-body ${contentLocked ? "bm-locked" : ""}`}>
        {activeTab === "settings" && (
          <Integrations
            role={role}
            connected={googleConnected}
            account={googleAccount}
            available={teacherAvailable}
            onAvailabilityChange={setTeacherAvailable}
            onConnected={(accountName) => {
              setGoogleConnected(true);
              if (accountName) setGoogleAccount(accountName);
            }}
            onDisconnected={() => {
              setGoogleConnected(false);
              setGoogleAccount("");
            }}
          />
        )}


        {activeTab === "upcoming" && (
          <UpcomingMeeting
            meetings={upcomingMeetings}
            loading={upcomingLoading}
            error={upcomingError}
            onChanged={bumpRefresh}
            highlightMeetingId={location.state?.highlightMeetingId}
          />
        )}

        {activeTab === "history" && <MeetingHistory refreshKey={refreshKey} highlightMeetingId={location.state?.highlightMeetingId} />}
      </div>

      {success && (
        <ScheduleSuccess meeting={success} onClose={() => setSuccess(null)} />
      )}

      <FloatingAIButton
        onClick={() => {
          dispatch(setDrawerState(!chatDrawerState.isOpen));
          dispatch(setIsWebAllowed(true));
          dispatch(setQueryMetaData(null));
        }}
      />      
    </div>
  );
};

export default Meetings;
